import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons/faCircleInfo';

import '../i18n/config';
import { ParentSearchBar, ValidationService } from 'us-web-services';
import { distanceOptions, frequencyOptions, lookingOptions } from '../constants/Search';
import { DropdownMenu } from '../DropdownMenu';

type Props = {
  changeCallback(values: ParentSearchBar): void;
  trackingCallback(event: string, params?: Record<string, unknown>): Promise<void>;
  search?: ParentSearchBar;
  locationOnly?: boolean;
  locationTitle?: string;
  distanceTitle?: string;
};

export const SearchBar = (props: Props): JSX.Element => {
  const { t } = useTranslation();
  const { changeCallback, search, trackingCallback, locationOnly, distanceTitle, locationTitle } = props;

  const [isValidZip, setIsValidZip] = React.useState<boolean>(ValidationService.isValidZip(search?.zip ?? ''));
  const [distance, setDistance] = React.useState<number>(search?.distance ?? 25);
  const [frequency, setFrequency] = React.useState<number | undefined>(search?.searchType?.id);
  const [lookingFor, setLookingFor] = React.useState<number | undefined>(search?.vertical?.id);
  const [zipCode, setZipCode] = React.useState<string | undefined>(search?.zip);

  const handleInputChange = (inputName: string, event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const searchProps = { distance, frequency, lookingFor, zipCode };
    const value = event.target?.value;

    const trackingProps = {
      elementId: 'search-bar',
      inputName,
      value,
    };

    let isNewZipValid = isValidZip;

    switch (inputName) {
      case 'distance':
        setDistance(parseInt(value, 10));
        searchProps.distance = parseInt(value, 10);
        break;

      case 'frequency':
        setFrequency(parseInt(value, 10));
        searchProps.frequency = parseInt(value, 10);
        break;

      case 'lookingFor':
        setLookingFor(parseInt(value, 10));
        searchProps.lookingFor = parseInt(value, 10);
        break;

      case 'zipCode':
        isNewZipValid = ValidationService.isValidZip(value);
        setIsValidZip(isNewZipValid);
        setZipCode(value);
        searchProps.zipCode = value;
        break;

      default:
        break;
    }

    // Only fire callbacks when zip is valid
    if (isNewZipValid) {
      trackingCallback('clicked-input', trackingProps);

      const updatedSearchBar: ParentSearchBar = {
        ...search, // retain passed-in parentId, userId when set
        distance: searchProps.distance,
        vertical: {
          id: searchProps.lookingFor || 1,
        },
        zip: searchProps.zipCode,
      };

      if (searchProps.frequency && searchProps.frequency > 0) {
        updatedSearchBar.searchType = {
          id: searchProps.frequency,
        };
      } else {
        updatedSearchBar.searchType = undefined;
      }

      changeCallback(updatedSearchBar);
    }
  };

  return (
    <div className='overflow-x-scroll sm:overflow-x-auto'>
      <div className='flex gap-3 pb-3 md:pb-1'>
        {
          !locationOnly && (
            <>
              <DropdownMenu
                className='min-w-[243px]'
                id='search-bar-looking-for'
                label={t('components.search.lookingFor')}
                items={lookingOptions} selectedValue={lookingFor}
                onChange={(e): void => handleInputChange('lookingFor', e)}
              />
              <DropdownMenu
                className='min-w-[122px]'
                id='search-bar-frequency'
                label={t('components.search.frequency')}
                items={frequencyOptions}
                selectedValue={frequency}
                onChange={(e): void => handleInputChange('frequency', e)}
              />
            </>
          )
        }
        <div className='block min-w-[90px]'>
          <label htmlFor='search-bar-zip' className='text-sm font-normal text-gray-500 pl-2'>
            {locationTitle || t('components.search.location')}
          </label>
          <div className='rounded-full border border-gray-300 bg-white shadow-md'>
            <input
              className={`bg-transparent rounded-full outline-none text-sm w-full ${isValidZip ? 'border-0' : 'focus:ring-red-500 border-1 border-red-500'}`}
              id='search-bar-zip'
              maxLength={5}
              onChange={(e): void => handleInputChange('zipCode', e)}
              placeholder={locationTitle || t('components.search.location')}
              size={locationOnly ? 9 : 6}
              type='text'
              value={zipCode}
            />
          </div>
        </div>
        <DropdownMenu
          className='min-w-[107px]'
          id='search-bar-distance'
          label={distanceTitle || t('components.search.distance')}
          items={distanceOptions}
          selectedValue={distance}
          onChange={(e): void => handleInputChange('distance', e)}
        />
      </div>
      {zipCode && !isValidZip && (<div className='block mt-[5px] ml-[340px] min-w-[176px] text-xs text-red-500'>
        <FontAwesomeIcon className='mr-1' icon={faCircleInfo} />{t('components.search.zipError')}
      </div>)}
    </div>
  );
};
